import React from 'react';
import { Box, Typography, Button } from '@mui/material';

interface HeroBlockProps {
  data: {
    title: string;
    subtitle: string;
    backgroundImage: string;
    overlayOpacity: number;
    ctaText?: string;
    ctaLink?: string;
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
    watermark?: {
      url: string;
      position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
      opacity: number;
    };
  };
}

const HeroBlock: React.FC<HeroBlockProps> = ({ data, theme }) => {
  const {
    title,
    subtitle,
    backgroundImage,
    overlayOpacity = 0.4,
    ctaText,
    ctaLink
  } = data;

  const watermark = theme?.watermark;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: '60vh', md: '80vh' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Dark Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'black',
          opacity: overlayOpacity,
          zIndex: 1
        }}
      />

      {/* Watermark */}
      {watermark?.url && (
        <Box
          component="img"
          src={watermark.url}
          alt="Watermark"
          sx={{
            position: 'absolute',
            opacity: watermark.opacity ?? 0.15,
            width: { xs: '80px', sm: '100px', md: '120px' },
            pointerEvents: 'none',
            zIndex: 1.5,
            ...(watermark.position === 'top-left' && { top: 16, left: 16 }),
            ...(watermark.position === 'top-right' && { top: 16, right: 16 }),
            ...(watermark.position === 'bottom-left' && { bottom: 16, left: 16 }),
            ...(watermark.position === 'bottom-right' && { bottom: 16, right: 16 })
          }}
        />
      )}

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          px: { xs: 3, md: 6 },
          maxWidth: '1200px'
        }}
      >
        <Typography
          variant="h1"
          sx={{
            color: '#ffffff !important',
            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5.5rem' },
            fontWeight: 700,
            mb: 2,
            textShadow: '0 4px 12px rgba(0,0,0,0.5)',
            lineHeight: 1.2
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant="h2"
            sx={{
              color: '#ffffff !important',
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
              fontWeight: 400,
              mb: 4,
              textShadow: '0 2px 8px rgba(0,0,0,0.5)',
              opacity: 0.95
            }}
          >
            {subtitle}
          </Typography>
        )}

        {ctaText && ctaLink && (
          <Button
            variant="contained"
            size="large"
            href={ctaLink}
            sx={{
              bgcolor: theme?.accentColor || '#FF0000',
              color: '#ffffff !important',
              px: 5,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
              '&:hover': {
                bgcolor: theme?.accentColor || '#FF0000',
                opacity: 0.9,
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 32px rgba(0,0,0,0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {ctaText}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default HeroBlock;